<template>
  <div class='authentications-layout'>
    <div class='authentications-layout__header'>
      <div class='authentications-layout__header__title'>
        {{ $t('authentications.layout.title') }}
      </div>
      <div class='authentications-layout__header__add-new-btn'>
        <v-btn class="new-authentication-button" color="primary" @click="showAddAuthPopup=true">
          {{ $t('authentications.layout.add.new.authentication.btn') }}
        </v-btn>
      </div>
    </div>
    <div class='authentications-layout__content'>
      <wombat-table
        :items="objectsList"
        :columns="tableColumns"
        :resize="false"
        :loadingItems="true"
        :columnsReorder="false"
        :itemHeight="58"
        :noResultsMessage="$t('noResultsFound')"
        name='authTable'
      >
        <component
          slot="row-cell"
          class="row-cell"
          slot-scope="rowCell"
          :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
          :item="rowCell.item"
          :column="rowCell.column"
          @editAuthObjectConfig='editAuthObjectConfig'
          @deleteAuthentication="openDeleteAuthenticationPopup"
          clickEditEventName='editAuthObjectConfig'
          clickDeleteEventName='deleteAuthentication'
        />
      </wombat-table>
    </div>
    <div class='authentications-layout__header top-delimiter'>
      <div class='authentications-layout__header__title'>
        {{ $t('authentications.layout.api.keys.title') }}
      </div>
      <div class='authentications-layout__header__add-new-btn'>
        <v-btn class="new-authentication-button secondary-btn" @click="showAddApiKeyPopup=true">
          {{ $t('authentications.layout.add.new.api.key.btn') }}
        </v-btn>
      </div>
    </div>
    <div class='authentications-layout__content'>
      <wombat-table
        :items="apiKeysItems"
        :columns="apiTableColumns"
        :resize="false"
        :loadingItems="true"
        :columnsReorder="false"
        :itemHeight="58"
        :noResultsMessage="$t('noResultsFound')"
        name='apiTable'
      >
        <component
          slot="row-cell"
          class="row-cell"
          slot-scope="rowCell"
          :is="apiTableRowCellComponentsHash[rowCell.column.name] || apiTableRowCellComponentsHash.default"
          :item="rowCell.item"
          :column="rowCell.column"
          @deleteApiKey='openDeleteApiKeyPopup'
          clickEventName='deleteApiKey'
        />
      </wombat-table>
    </div>
    <add-new-authentication-object-popup
      :visible='showAddAuthPopup'
      :editData='getEditData'
      :editMode='editMode'
      @closed='closeClicked'
      @save='saveClicked'
    />
    <add-api-key-popup
      :visible-popup='showAddApiKeyPopup'
      @closed='closeAddApiKeyPopupClicked'
      @save='saveAddApiKeyPopupClicked'
    />
    <yes-no-popup
      :visible-popup='displayDeleteApiKeyPopup || displayDeleteAuthenticationPopup'
      :title='yesNoPopupTitle'
      :message='yesNoPopupMessage'
      :yesCaption='yesNoPopupYesCaption'
      :noCaption='yesNoPopupNoCaption'
      @clicked='handleButtonClicked'
    />
  </div>
</template>

<script>
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import AddNewAuthenticationObjectPopup from '@/components/popup/AddNewAuthenticationObjectPopup.vue';
import {
  ADD_URL_CONFIGURATIONS, DELETE_CONFIGURATION,
  LOAD_CREDENTIALS_FROM_CONFIGURATION,
} from '@/store/project/actionTypes';
import { CONFIGURATION, CONFIGURATION_ACTION_TYPES } from '@/constants/configuration';
import { mapGetters } from 'vuex';
import { apiKeysColumns, authenticationObjectsColumns, getEmptyColumn } from '@/services/tableColumns';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import IntegrationActionTableCell from '@/components/tableCells/integrationsTableCells/IntegrationActionTableCell.vue';
import AuthenticationObjectIconTableCell from '@/components/tableCells/authObjectsTableCells/AuthenticationObjectIconTableCell.vue';
import AuthenticationObjectTypeTableCell from '@/components/tableCells/authObjectsTableCells/AuthenticationObjectTypeTableCell.vue';
import _ from 'lodash';
import ApiKeyTableCell from '@/components/tableCells/apiKeysTableCells/ApiKeyIconTableCell.vue';
import { ADD_API_KEY, DELETE_API_KEY, LOAD_API_KEYS } from '@/store/apiKeys/actionTypes';
import AddApiKeyPopup from '@/components/popup/AddApiKeyPopup.vue';
import ApiKeyUserTableCell from '@/components/tableCells/apiKeysTableCells/ApiKeyUserTableCell.vue';
import ApiKeyCreatedDateTableCell from '@/components/tableCells/apiKeysTableCells/ApiKeyCreatedDateTableCell.vue';
import ApiKeyActionTableCell from '@/components/tableCells/apiKeysTableCells/ApiKeyActionTableCell.vue';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import APIKeyExpirationTableCell from '@/components/tableCells/apiKeysTableCells/APIKeyExpirationTableCell.vue';

export default {
  name: 'AuthenticationsLayout',
  components: {
    AddApiKeyPopup, WombatTable, AddNewAuthenticationObjectPopup, YesNoPopup,
  },
  data() {
    return {
      editMode: false,
      showAddAuthPopup: false,
      showAddApiKeyPopup: false,
      addAuthPopupData: null,
      displayDeleteApiKeyPopup: false,
      displayDeleteAuthenticationPopup: false,
      apiKeyObjectToDelete: null,
      authenticationObjectToDelete: null,
    };
  },
  async mounted() {
    await this.$store.dispatch(LOAD_CREDENTIALS_FROM_CONFIGURATION);
    await this.$store.dispatch(LOAD_API_KEYS);
  },
  computed: {
    ...mapGetters(['domains', 'configurationAuthenticationObjects', 'apiKeys']),
    objectsList() {
      return this.configurationAuthenticationObjects.map(authObject => ({
        id: authObject.id,
        name: authObject.settingsMetaData.name,
        description: authObject.settingsMetaData.description,
        credentials: authObject.settingsMetaData.credentials,
        type: authObject.settingsMetaData.type,
      }));
    },
    apiKeysItems() {
      return this.apiKeys.map(apiKey => ({
        id: apiKey.id,
        createdDate: apiKey.createdDate,
        description: apiKey.description,
        expiration: apiKey.expiration,
        user: apiKey.user,
      })).sort((a, b) => (a.createdDate > b.createdDate ? -1 : 1));
    },
    tableColumns() {
      const columns = [
        authenticationObjectsColumns.icon,
        authenticationObjectsColumns.name,
        authenticationObjectsColumns.type,
        authenticationObjectsColumns.description,
        authenticationObjectsColumns.actions,
      ]
        .filter(column => column)
        .map(column => ({
          ...column,
          title: column.title && this.$t(column.title),
        }));
      return [getEmptyColumn(32), ...columns, getEmptyColumn(32)];
    },
    tableRowCellComponentsHash() {
      return {

        icon: AuthenticationObjectIconTableCell,
        name: WombatRowDefaultCell,
        actions: IntegrationActionTableCell,
        description: WombatRowDefaultCell,
        type: AuthenticationObjectTypeTableCell,
        default: WombatRowDefaultCell,
      };
    },
    apiTableColumns() {
      const columns = [
        apiKeysColumns.icon,
        apiKeysColumns.user,
        apiKeysColumns.createdDate,
        apiKeysColumns.expiration,
        apiKeysColumns.description,
        apiKeysColumns.actions,
      ]
        .filter(column => column)
        .map(column => ({
          ...column,
          title: column.title && this.$t(column.title),
        }));
      return [getEmptyColumn(32), ...columns, getEmptyColumn(32)];
    },
    apiTableRowCellComponentsHash() {
      return {
        icon: ApiKeyTableCell,
        user: ApiKeyUserTableCell,
        actions: ApiKeyActionTableCell,
        description: WombatRowDefaultCell,
        createdDate: ApiKeyCreatedDateTableCell,
        expiration: APIKeyExpirationTableCell,
        default: WombatRowDefaultCell,
      };
    },
    getEditData() {
      return _.cloneDeep(this.addAuthPopupData);
    },
    yesNoPopupTitle() {
      return this.displayDeleteApiKeyPopup ? 'delete.api.key.popup.title' : 'delete.authentication.popup.title';
    },
    yesNoPopupMessage() {
      return this.displayDeleteApiKeyPopup ? 'delete.api.key.popup.message' : 'delete.authentication.popup.message';
    },
    yesNoPopupYesCaption() {
      return this.displayDeleteApiKeyPopup ? 'delete.api.key.popup.yes' : 'delete.authentication.popup.yes';
    },
    yesNoPopupNoCaption() {
      return this.displayDeleteApiKeyPopup ? 'delete.api.key.popup.no' : 'delete.authentication.popup.no';
    },
  },
  methods: {
    async saveClicked(data) {
      this.showAddAuthPopup = false;
      this.addAuthPopupData = null;
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: [{
          data: '', metaData: [], id: data.id, settingsMetaData: data, tld: this.domains[0],
        }],
        configType: CONFIGURATION.CONFIG_TYPES.CREDENTIALS,
        scanEmployees: false,
        successMessageType: CONFIGURATION_ACTION_TYPES.ADD_CREDENTIALS,
      });
    },
    async saveAddApiKeyPopupClicked(data) {
      await this.$store.dispatch(ADD_API_KEY, data);
    },
    closeClicked() {
      this.editMode = false;
      this.showAddAuthPopup = false;
      this.addAuthPopupData = null;
    },
    closeAddApiKeyPopupClicked() {
      this.showAddApiKeyPopup = false;
    },
    editAuthObjectConfig(authObject) {
      this.addAuthPopupData = authObject;
      this.editMode = true;
      this.showAddAuthPopup = true;
    },
    async openDeleteApiKeyPopup(apiKeyObject) {
      this.apiKeyObjectToDelete = apiKeyObject;
      this.displayDeleteApiKeyPopup = true;
    },
    async openDeleteAuthenticationPopup(authenticationObject) {
      this.authenticationObjectToDelete = authenticationObject;
      this.displayDeleteAuthenticationPopup = true;
    },
    async deleteApiKeyAction() {
      await this.$store.dispatch(DELETE_API_KEY, { apiKeyId: this.apiKeyObjectToDelete.id });
    },
    async deleteAuthenticationAction() {
      await this.$store.dispatch(DELETE_CONFIGURATION, {
        payload: [{data: this.authenticationObjectToDelete.id}],
        configType: CONFIGURATION.CONFIG_TYPES.CREDENTIALS,
        successMessageType: CONFIGURATION_ACTION_TYPES.DELETE_CREDENTIALS,
      });
    },
    async deleteApiKeyPopupClicked(value) {
      if (value) {
        await this.deleteApiKeyAction();
      }
      this.displayDeleteApiKeyPopup = false;
      this.apiKeyObjectToDelete = null;
    },
    async deleteAuthenticationPopupClicked(value) {
      if (value) {
        await this.deleteAuthenticationAction();
      }
      this.displayDeleteAuthenticationPopup = false;
      this.authenticationObjectToDelete = null;
    },
    async handleButtonClicked(value) {
      if (this.displayDeleteApiKeyPopup) {
        await this.deleteApiKeyPopupClicked(value);
      } else {
        await this.deleteAuthenticationPopupClicked(value);
      }
    },
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/styles/design-system";

.authentications-layout {
  padding-left: $space-8;
  padding-top: $space-8;
  padding-right: $space-8;

  .top-delimiter {
    margin-top: $space-8;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__title {
      margin-bottom: $space-8;
      color: var(--text-primary);
      font-family: $default-application-font-bold;
      font-size: $font-size-2xl;
      font-weight: bold;
      line-height: $line-height-tight;
      letter-spacing: $letter-spacing-normal;
    }

    &__add-new-btn {
      justify-self: right;

      .new-authentication-button.v-btn.v-btn.v-btn {
        display: inline-block;
        cursor: pointer;
        height: $btn-height-md;
        width: rem(170);
        border-radius: $radius-sm;
        background-color: var(--brand-primary);
        color: #ffffff;
        font-size: $font-size-base;
        font-family: $font-family-medium;
        letter-spacing: $letter-spacing-wide;
        line-height: $line-height-tight;
        text-align: center;
        text-transform: uppercase;

        &.secondary-btn {
          background-color: transparent !important;
          border: 1px solid var(--brand-primary) !important;
          color: var(--brand-primary) !important;
        }
      }
    }
  }
}
</style>

<style lang='scss'>
@import "~@/assets/styles/design-system";

.authentications-layout {
  .wombat-table {
    --displayactioncell: none;
    padding: $space-4 0;

    .wombat-header {
      padding-bottom: $space-2;
    }

    .wombat-row.wombat-row {
      border-top: none;
      border-left: none;
      border-right: none;
    }

    .wombat-overlay {
      &:hover {
        background-color: var(--bg-tertiary);
        --display: block;
        --bg: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, var(--bg-tertiary) 100%);
        --displayactioncell: flex;
      }
    }

    .row-cell {
      @include truncate;
    }
  }
}
</style>
